import {
  Consent,
  ConsentAnswers,
  ConsentUpdate,
  ShopifyConsentKey,
  ShopifyVisitorConsent,
} from "lib/types/consent";
import { eventTypes, GET_SHOPIFY_CONSENT_TIMEOUT_MS } from "lib/utils/constants";
import { getCurrentRealEstate, publishPostMessage } from "lib/utils/helpers";

// sale_of_data is never synced with Shopify.
const SYNCED_SHOPIFY_KEYS: ShopifyConsentKey[] = ["analytics", "marketing", "preferences"];

/** Shopify keys a consent controls, from metadata.shopify_linked_attribute */
export const getShopifyLinkedKeys = (consent: Consent): ShopifyConsentKey[] =>
  (consent?.metadata?.shopify_linked_attribute ?? []).filter((key) => SYNCED_SHOPIFY_KEYS.includes(key));

/** Has its own checkbox on the login screens, with every channel (option) following it. */
export const MARKETING_CONSENT_CODE = "MARKETING";

/** Consents answered and sent on the login screens: everything stored on the server, hidden ones included */
export const getStoredConsents = (consents: Consent[] = []): Consent[] =>
  consents.filter((consent) => consent?.storage_mode === "SERVER");

/**
 * Consents in the Manage preferences list and under the main checkbox: those
 * linked to at least one Shopify key. shopflo_hidden: true always hides one.
 * MARKETING has no Shopify link, so it stays out and keeps its own checkbox.
 */
export const getVisibleConsents = (consents: Consent[] = []): Consent[] =>
  getStoredConsents(consents).filter((consent) => {
    const linkedKeys = consent?.metadata?.shopify_linked_attribute;
    return Array.isArray(linkedKeys) && linkedKeys.length > 0 && !consent?.metadata?.shopflo_hidden;
  });

export const getMarketingConsent = (consents: Consent[] = []): Consent | null =>
  getStoredConsents(consents).find((consent) => consent.code === MARKETING_CONSENT_CODE) ?? null;

/** Only "yes", in any case, counts as granted */
export const isShopifyConsentGranted = (value?: string | null): boolean =>
  String(value ?? "").toLowerCase() === "yes";

/**
 * Starting answers for the login screens. Mandatory consents are always on; the
 * rest are on when any Shopify key they link to is granted. MARKETING has no
 * Shopify key, so it starts on only when it and every one of its channels are
 * default_selected.
 */
export const getAnswersFromShopify = (
  consents: Consent[],
  shopifyConsent: ShopifyVisitorConsent | null,
): ConsentAnswers =>
  getStoredConsents(consents).reduce<ConsentAnswers>((answers, consent) => {
    if (consent.code === MARKETING_CONSENT_CODE) {
      answers[consent.code] =
        consent.is_mandatory ||
        (Boolean(consent.default_selected) &&
          (consent.options ?? []).every((option) => option.default_selected));
      return answers;
    }
    answers[consent.code] =
      consent.is_mandatory ||
      getShopifyLinkedKeys(consent).some((key) => isShopifyConsentGranted(shopifyConsent?.[key]));
    return answers;
  }, {});

/**
 * Login screen answers after a Shopify consent change (e.g. its cookie banner):
 * consents linked to a Shopify key follow it (mandatory ones stay on); the rest,
 * like MARKETING, keep the shopper's current answer.
 */
export const applyShopifyConsentToAnswers = (
  consents: Consent[],
  answers: ConsentAnswers,
  shopifyConsent: ShopifyVisitorConsent | null,
): ConsentAnswers =>
  getStoredConsents(consents).reduce<ConsentAnswers>(
    (nextAnswers, consent) => {
      const linkedKeys = getShopifyLinkedKeys(consent);
      if (!linkedKeys.length) return nextAnswers;
      nextAnswers[consent.code] =
        consent.is_mandatory || linkedKeys.some((key) => isShopifyConsentGranted(shopifyConsent?.[key]));
      return nextAnswers;
    },
    { ...answers },
  );

/**
 * Every consent in the Manage preferences list on or off (mandatory ones stay on).
 * Hidden consents and MARKETING keep their current answers.
 */
export const getAllAnswers = (
  consents: Consent[],
  isAccepted: boolean,
  currentAnswers: ConsentAnswers = {},
): ConsentAnswers =>
  getVisibleConsents(consents).reduce<ConsentAnswers>(
    (answers, consent) => {
      answers[consent.code] = consent.is_mandatory || isAccepted;
      return answers;
    },
    { ...currentAnswers },
  );

/** Answers from a shopper's saved consents, skipping unanswered ones */
export const getAnswersFromConsents = (consents: Consent[] = []): ConsentAnswers =>
  consents.reduce<ConsentAnswers>((answers, consent) => {
    if (typeof consent?.is_accepted === "boolean") answers[consent.code] = consent.is_accepted;
    return answers;
  }, {});

/**
 * Shopify keys for setTrackingConsent. A key shared by several consents is
 * granted if any of them is; keys no answered consent links to are left out.
 */
export const getShopifyConsentFromAnswers = (
  consents: Consent[],
  answers: ConsentAnswers,
): Partial<Record<ShopifyConsentKey, boolean>> =>
  consents.reduce<Partial<Record<ShopifyConsentKey, boolean>>>((shopifyConsent, consent) => {
    if (!(consent.code in answers)) return shopifyConsent;
    getShopifyLinkedKeys(consent).forEach((key) => {
      shopifyConsent[key] = Boolean(shopifyConsent[key]) || Boolean(answers[consent.code]);
    });
    return shopifyConsent;
  }, {});

/**
 * customer_consents for every stored consent, hidden ones included. MARKETING
 * sends each channel with its own value; other options are left to updateConsent.
 */
export const getConsentUpdatesFromAnswers = (consents: Consent[], answers: ConsentAnswers): ConsentUpdate[] =>
  getStoredConsents(consents).map((consent) => {
    const isAccepted = consent.is_mandatory || Boolean(answers[consent.code]);
    const update: ConsentUpdate = { code: consent.code, is_accepted: isAccepted };
    if (consent.code === MARKETING_CONSENT_CODE && consent.options?.length) {
      update.options = consent.options.reduce<Record<string, boolean>>((options, option) => {
        options[option.code] = isAccepted;
        return options;
      }, {});
    }
    return update;
  });

/**
 * Merges partial updates onto a shopper's saved consents, so the PUT carries the
 * whole form. Mandatory consents are always sent accepted; unanswered consents
 * that are not being updated are left out, as are codes the merchant does not offer.
 */
export const mergeConsentUpdates = (
  saved: Consent[] = [],
  updates: ConsentUpdate[] = [],
): ConsentUpdate[] => {
  const updatesByCode = new Map(updates.map((update) => [update.code, update]));

  return saved
    .filter((consent) => consent?.storage_mode === "SERVER")
    .flatMap((consent) => {
      const update = updatesByCode.get(consent.code);
      const isAccepted = consent.is_mandatory ? true : update ? update.is_accepted : consent.is_accepted;
      if (typeof isAccepted !== "boolean") return [];

      const merged: ConsentUpdate = { code: consent.code, is_accepted: isAccepted };
      // A declined consent clears its options, so they are only sent when accepted.
      if (isAccepted && consent.options?.length) {
        const options = consent.options.reduce<Record<string, boolean>>((acc, option) => {
          const value = update?.options?.[option.code] ?? option.is_accepted;
          if (typeof value === "boolean") acc[option.code] = value;
          return acc;
        }, {});
        if (Object.keys(options).length) merged.options = options;
      }
      return [merged];
    });
};

/**
 * Asks the storefront script for Shopify's currentVisitorConsent(). Resolves
 * null when the script does not answer in time (no script, or no Shopify API).
 */
export const requestShopifyConsent = (): Promise<ShopifyVisitorConsent | null> =>
  new Promise((resolve) => {
    let settled = false;

    const timer = setTimeout(() => {
      if (settled) return;
      settled = true;
      window.removeEventListener("message", handler);
      resolve(null);
    }, GET_SHOPIFY_CONSENT_TIMEOUT_MS);

    const handler = (event: MessageEvent) => {
      if (!event.data || event.data.type !== eventTypes.SET_SHOPIFY_CONSENT) return;
      if (settled) return;
      settled = true;
      clearTimeout(timer);
      window.removeEventListener("message", handler);
      resolve(event.data.payload ?? null);
    };

    window.addEventListener("message", handler);
    publishPostMessage(eventTypes.GET_SHOPIFY_CONSENT, { source: getCurrentRealEstate() });
  });

/** Has the storefront script write these keys to Shopify with setTrackingConsent */
export const publishShopifyConsent = (consent: Partial<Record<ShopifyConsentKey, boolean>>) => {
  if (!Object.keys(consent).length) return;
  publishPostMessage(eventTypes.FLO_CONSENT_UPDATED, { consent });
};

/** Consent codes the shopper has accepted: their saved consents once logged in, the login screen answers before. */
export const getAcceptedConsentCodes = (
  userConsents: Consent[] | null | undefined,
  answers: ConsentAnswers = {},
): string[] =>
  userConsents
    ? userConsents.filter((consent) => consent.is_accepted === true).map((consent) => consent.code)
    : Object.keys(answers).filter((code) => answers[code]);

/**
 * Same rule as the storefront script: with consent enabled on merchant-apps and
 * analytics.global enabled, a downstream needs one of its global.consents.
 * Missing downstreams fall back to "*"; an empty list needs no consent.
 * Otherwise every downstream sends as today.
 */
export const canSendToDownstream = ({
  isConsentEnabled,
  analyticsConfig,
  downstream,
  acceptedConsents,
}: {
  isConsentEnabled: boolean;
  analyticsConfig: any;
  downstream: string;
  acceptedConsents: string[];
}): boolean => {
  if (!isConsentEnabled || !analyticsConfig?.global?.enabled) return true;
  const consents: Record<string, string[]> = analyticsConfig.global.consents ?? {};
  const requiredConsents = consents[downstream] ?? consents["*"] ?? [];
  if (!requiredConsents.length) return true;
  return requiredConsents.some((code) => acceptedConsents.includes(code));
};
